import { useRef, useState, useEffect } from 'react';
import { Volume2, VolumeX, Maximize2, Play } from 'lucide-react';
import useIsMobile from '@/hooks/useIsMobile';
import { useLanguage } from '@/lib/LanguageContext';

/**
 * Reproductor de YouTube embebido con el mismo formato que VideoPlayer:
 * - Formato 16:9 (aspect-video), fondo blanco, rounded-2xl
 * - Logo SmartPOS superpuesto
 * - Botón personalizado de activar/desactivar sonido (vía YouTube IFrame API)
 * - Botón personalizado de pantalla completa (sobre el contenedor)
 */
const LOGO_SMARTPOS = "https://media.base44.com/images/public/6a877e598e2aae01ac439787/966e8fe32_Logo6.png/v1/fit/w_200,h_64,q_90,usm_0.66_1.00_0.01,enc_webp,quality_auto/Logo6.webp";

let apiPromise = null;
function loadYouTubeAPI() {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) { resolve(); return; }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve();
    };
    if (!document.getElementById('yt-iframe-api')) {
      const tag = document.createElement('script');
      tag.id = 'yt-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      document.head.appendChild(tag);
    }
  });
  return apiPromise;
}

export default function VideoCard({ videoId, title = 'Vídeo', className = '', aspectClass = 'aspect-video' }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [shouldLoad, setShouldLoad] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  const [userStarted, setUserStarted] = useState(false);
  const ready = shouldLoad && (!isMobile || userStarted);

  // Lazy-load: no crea el reproductor hasta estar cerca del viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) { setShouldLoad(true); observer.disconnect(); }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!ready || !videoId) return;
    let cancelled = false;
    loadYouTubeAPI().then(() => {
      if (cancelled || !containerRef.current) return;
      const host = document.createElement('div');
      host.style.width = '100%';
      host.style.height = '100%';
      containerRef.current.appendChild(host);
      playerRef.current = new window.YT.Player(host, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 1, mute: 1, loop: 1, playlist: videoId,
          controls: 0, rel: 0, modestbranding: 1, playsinline: 1, iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            try {
              const f = e.target.getIframe();
              f.title = title;
              f.style.position = 'absolute';
              f.style.inset = '0';
              f.style.width = '100%';
              f.style.height = '100%';
            } catch { /* noop */ }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      try { playerRef.current?.destroy?.(); } catch { /* noop */ }
      playerRef.current = null;
      if (containerRef.current) {
        containerRef.current.querySelectorAll('iframe').forEach((f) => f.remove());
      }
    };
  }, [ready, videoId, title]);

  const toggleMute = () => {
    const p = playerRef.current;
    if (!p || typeof p.unMute !== 'function') return;
    if (muted) { p.unMute(); p.playVideo?.(); setMuted(false); }
    else { p.mute(); setMuted(true); }
  };

  const handleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    }
  };

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${aspectClass} bg-white rounded-2xl ${className}`}>
      {/* Logo SmartPOS superpuesto */}
      <div className="absolute top-4 left-4 z-10 pointer-events-none">
        <img
          src={LOGO_SMARTPOS}
          alt="SmartPOS"
          className="h-8 w-auto drop-shadow-lg"
          draggable={false}
          loading="lazy"
          decoding="async"
        />
      </div>

      {/* Móvil: tocar para reproducir (miniatura + botón) */}
      {isMobile && !userStarted && (
        <>
          <img
            src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <button
            onClick={() => setUserStarted(true)}
            className="absolute inset-0 z-20 flex items-center justify-center bg-black/25"
            aria-label={t('aria.playVideo')}
          >
            <span className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
              <Play size={28} className="text-white ml-1" fill="currentColor" />
            </span>
          </button>
        </>
      )}

      {/* Controles: sonido + pantalla completa */}
      <div className="absolute bottom-4 right-4 flex gap-2 z-10">
        <button
          onClick={toggleMute}
          className="w-10 h-10 rounded-full glassmorphism flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label={muted ? t('aria.mute') : t('aria.unmute')}
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        <button
          onClick={handleFullscreen}
          className="w-10 h-10 rounded-full glassmorphism flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label={t('aria.fullscreen')}
        >
          <Maximize2 size={18} />
        </button>
      </div>
    </div>
  );
}